import React from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Card, Typography } from 'antd';
import { DownloadOutlined, PrinterOutlined, EditOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { LRCopy } from 'pages/lrmanager/LRView/LRview';
import { LRPrint } from 'pages/lrmanager/LRView/components/LRPrint';
import { ACL } from 'components/common';

const { Text } = Typography;

function LROverviewTab({
	lrDetails,
	lrLoading,
	lrError,
	handleDownload,
	handlePrint,
	tripDetails,
	tripId,
	navigate,
	printVisible,
	LRPrintRef,
	selectedLRDetails,
	isPDFPrint,
}) {
	const location = useLocation();
	const isEnded = tripDetails?.endTime;
	return (
		<>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: 24,
					padding: '0 0 24px 0',
					background: 'transparent',
					borderRadius: 8,
					minHeight: 56,
					borderBottom: '1px solid #eaeaea',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					<ACL action="search" module="consignment_note">
						<Button
							type="default"
							icon={<DownloadOutlined />}
							onClick={handleDownload}
							disabled={!lrDetails}
							style={{
								minWidth: 160,
								fontWeight: 500,
								border: '2px solid #1890ff',
								color: '#1890ff',
								background: '#fff',
							}}
						>
							DOWNLOAD LR
						</Button>
					</ACL>
					<ACL action="search" module="consignment_note">
						<Button
							type="default"
							icon={<PrinterOutlined />}
							onClick={handlePrint}
							disabled={!lrDetails}
							style={{
								minWidth: 160,
								fontWeight: 500,
								border: '2px solid #1890ff',
								color: '#1890ff',
								background: '#fff',
							}}
						>
							PRINT LR
						</Button>
					</ACL>
				</div>
				<div
					style={{
						minWidth: 120,
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					{lrDetails ? (
						<ACL action="update" module="consignment_note">
							<Button
								type="primary"
								onClick={() => {
									// Combine trip details and LR details for auto-population
									const combinedData = {
										...tripDetails,
										...lrDetails,
										// Map LR fields to form fields
										project_id: lrDetails.project_id || tripDetails.project_id,
										vendor_id: lrDetails.vendor_id || tripDetails.vendor_id,
										driver_name:
											lrDetails.driver_name || tripDetails.driver_name,
										vehicle_number:
											lrDetails.vehicle_number || tripDetails.vehicle_number,
										// Sender details
										sender_name: lrDetails.sender_name,
										sender_address: lrDetails.sender_address,
										sender_pincode: lrDetails.sender_pincode,
										source_location: lrDetails.source_location,
										// Receiver details
										receiver_name: lrDetails.receiver_name,
										receiver_address: lrDetails.receiver_address,
										receiver_pincode: lrDetails.receiver_pincode,
										destination_location: lrDetails.destination_location,
										// Goods details
										goods_description: lrDetails.goods_description,
										declared_value: lrDetails.declared_value,
										gross_weight: lrDetails.gross_weight,
										unit_count: lrDetails.unit_count,
										goods_details: lrDetails.goods_details,
										// Additional fields
										client_reference_numbers:
											lrDetails.client_reference_numbers,
										ewb_number: lrDetails.ewb_number,
										// Trip fields for context
										trip_type: tripDetails.trip_type,
										city: tripDetails.city,
										vehicle_type: tripDetails.vehicleType,
										client_rate: tripDetails.clientRate,
										vendor_rate: tripDetails.vendorRate,
										// Map pincode fields correctly
										start_location_pincode: lrDetails.sender_pincode,
										end_location_pincode: lrDetails.receiver_pincode,
										consignment_note_id: lrDetails.id,
									};
									const encoded = encodeURIComponent(
										JSON.stringify(combinedData)
									);
									navigate(`/trips/${tripId}/create-lr?data=${encoded}`, {
										state: { from: location.pathname, isEdit: true },
									});
								}}
								hidden={isEnded}
								style={{
									minWidth: 120,
									fontWeight: 500,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<span
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 6,
									}}
								>
									<EditOutlined style={{ fontSize: 16, marginRight: 4 }} />
									EDIT LR
								</span>
							</Button>
						</ACL>
					) : (
						<ACL action="create" module="consignment_note">
							<Button
								type="primary"
								onClick={() => {
									const encoded = encodeURIComponent(JSON.stringify(tripDetails));
									navigate(`/trips/${tripId}/create-lr?data=${encoded}`, {
										state: { from: location.pathname },
									});
								}}
								style={{
									minWidth: 120,
									fontWeight: 500,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
								hidden={isEnded}
							>
								<span
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 6,
									}}
								>
									<EditOutlined style={{ fontSize: 16, marginRight: 4 }} />
									CREATE LR
								</span>
							</Button>
						</ACL>
					)}
				</div>
			</div>
			<Card
				style={{
					marginTop: 32,
					minHeight: 200,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{lrLoading && <Spin tip="Loading LR details..." />}
				{!lrLoading && lrError && <Text type="danger">{lrError}</Text>}
				{!lrLoading && !lrError && lrDetails && <LRCopy details={lrDetails} />}
				{!lrLoading && !lrError && !lrDetails && (
					<Text type="secondary" style={{ fontSize: 18 }}>
						No LR details found.
					</Text>
				)}
			</Card>
			{printVisible && (
				<div style={{ position: 'absolute', zIndex: -1 }}>
					<LRPrint
						ref={LRPrintRef}
						details={selectedLRDetails || {}}
						isvisible={+printVisible}
						ispdf={+isPDFPrint}
					/>
				</div>
			)}
		</>
	);
}

LROverviewTab.propTypes = {
	lrDetails: PropTypes.object,
	lrLoading: PropTypes.bool,
	lrError: PropTypes.string,
	handleDownload: PropTypes.func,
	handlePrint: PropTypes.func,
	tripDetails: PropTypes.object,
	tripId: PropTypes.string,
	navigate: PropTypes.func,
	printVisible: PropTypes.bool,
	LRPrintRef: PropTypes.object,
	selectedLRDetails: PropTypes.object,
	isPDFPrint: PropTypes.bool,
};

export default LROverviewTab;
